import { useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, endOfWeek, format, isSameDay, startOfDay, startOfWeek } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import { toAmsterdamDate, toAmsterdamTime } from '../../utils/dateUtils.js';
import { fetchActiecentrumData, UNLOGGED_SESSION_LOOKBACK_DAYS } from '../../utils/actiesUtils.js';

// Fitness tracking deel 5: hoeveel items er compact op het dashboardkaartje
// staan voordat "Bekijk alles" nodig is — de volledige (gepagineerde) lijst
// staat op NietGelogdeSessiesPage.
const UNLOGGED_PREVIEW_COUNT = 5;

const DEFAULT_PREFERENCES = { enabled: false, channel: 'push', weekday: 1, notification_time: '08:00' };

const WEEKDAYS = [
  [1, 'dashboard.weekdag.1'], [2, 'dashboard.weekdag.2'], [3, 'dashboard.weekdag.3'],
  [4, 'dashboard.weekdag.4'], [5, 'dashboard.weekdag.5'], [6, 'dashboard.weekdag.6'],
  [7, 'dashboard.weekdag.7'],
];

function birthdayInWeek(dateOfBirth, weekStart, weekEnd) {
  if (!dateOfBirth) return null;
  const [, month, day] = dateOfBirth.split('-').map(Number);
  if (!month || !day) return null;
  for (const year of [weekStart.getFullYear(), weekEnd.getFullYear()]) {
    const birthday = new Date(year, month - 1, day);
    if (birthday >= startOfDay(weekStart) && birthday <= weekEnd) return birthday;
  }
  return null;
}

function firstName(fullName) {
  return (fullName || '').trim().split(/\s+/)[0] || t('dashboard.coachFallback');
}

export default function DashboardPage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const { showSuccess } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [clients, setClients] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [enrollmentCounts, setEnrollmentCounts] = useState({});
  const [actionSummary, setActionSummary] = useState({ ptAanvragen: 0, prospectActies: 0, uitzonderingen: 0 });
  const [nietGelogdeSessies, setNietGelogdeSessies] = useState([]);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [preferenceOpen, setPreferenceOpen] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);

  const weekStart = useMemo(() => startOfWeek(new Date(), { weekStartsOn: 1 }), []);
  const weekEnd = useMemo(() => endOfWeek(new Date(), { weekStartsOn: 1 }), []);
  const tomorrowEnd = useMemo(() => addDays(startOfDay(new Date()), 2), []);

  const loadDashboard = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    try {
      const [profileRes, linksRes, lessonsRes, preferencesRes, actionData] = await Promise.all([
        supabase.from('profiles').select('full_name, email').eq('id', user.id).maybeSingle(),
        supabase.from('coach_clients').select('client_id').eq('coach_id', user.id).eq('status', 'active'),
        supabase.from('group_lessons')
          .select('id, name, starts_at, duration_minutes, max_participants, type, is_cancelled, coach_id, executing_coach_id, locations(name)')
          .gte('starts_at', weekStart.toISOString())
          .lt('starts_at', addDays(weekEnd, 1).toISOString())
          .or(`coach_id.eq.${user.id},executing_coach_id.eq.${user.id}`)
          .order('starts_at', { ascending: true }),
        supabase.from('coach_birthday_notification_preferences')
          .select('enabled, channel, weekday, notification_time').eq('coach_id', user.id).maybeSingle(),
        fetchActiecentrumData(user.id, role),
      ]);

      for (const response of [profileRes, linksRes, lessonsRes, preferencesRes]) {
        if (response.error && response.error.code !== 'PGRST116') throw response.error;
      }

      const clientIds = (linksRes.data || []).map(link => link.client_id).filter(Boolean);
      let clientData = [];
      if (clientIds.length > 0) {
        const clientsRes = await supabase.from('profiles')
          .select('id, full_name, date_of_birth, client_status').in('id', clientIds);
        if (clientsRes.error) throw clientsRes.error;
        clientData = clientsRes.data || [];
      }

      const lessonData = (lessonsRes.data || []).filter(lesson => !lesson.is_cancelled);
      const counts = {};
      if (lessonData.length > 0) {
        const enrollmentsRes = await supabase.from('lesson_enrollments')
          .select('lesson_id').in('lesson_id', lessonData.map(lesson => lesson.id)).eq('status', 'enrolled');
        if (enrollmentsRes.error) throw enrollmentsRes.error;
        for (const enrollment of enrollmentsRes.data || []) {
          counts[enrollment.lesson_id] = (counts[enrollment.lesson_id] || 0) + 1;
        }
      }

      setProfile(profileRes.data);
      setClients(clientData);
      setLessons(lessonData);
      setEnrollmentCounts(counts);
      setActionSummary({
        ptAanvragen: actionData.ptAanvragen.length,
        prospectActies: actionData.prospectActies.length,
        uitzonderingen: actionData.afgewezenTokens.length + actionData.verlopenTokens.length + actionData.incasso.length,
      });
      setNietGelogdeSessies(actionData.nietGelogdeSessies);
      if (preferencesRes.data) {
        setPreferences({
          ...DEFAULT_PREFERENCES,
          ...preferencesRes.data,
          notification_time: preferencesRes.data.notification_time?.slice(0, 5) || '08:00',
        });
      }
    } catch (err) {
      setError(t('dashboard.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id, role, weekStart, weekEnd]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const birthdays = useMemo(() => clients
    .map(client => ({ ...client, birthday: birthdayInWeek(client.date_of_birth, weekStart, weekEnd) }))
    .filter(client => client.birthday)
    .sort((a, b) => a.birthday - b.birthday), [clients, weekStart, weekEnd]);

  const upcomingLessons = useMemo(() => lessons.filter(lesson => {
    const date = toAmsterdamDate(lesson.starts_at);
    return date >= startOfDay(new Date()) && date < tomorrowEnd;
  }), [lessons, tomorrowEnd]);

  const activeClients = clients.filter(client => client.client_status === 'active').length;
  const groupLessons = lessons.filter(lesson => lesson.type !== 'personal');
  const totalCapacity = groupLessons.reduce((sum, lesson) => sum + (lesson.max_participants || 0), 0);
  const totalEnrollments = groupLessons.reduce((sum, lesson) => sum + (enrollmentCounts[lesson.id] || 0), 0);
  const openSpots = groupLessons.reduce((sum, lesson) => sum + Math.max(0, (lesson.max_participants || 0) - (enrollmentCounts[lesson.id] || 0)), 0);
  const occupancy = totalCapacity > 0 ? Math.round((totalEnrollments / totalCapacity) * 100) : 0;

  async function persistPreferences(nextPreferences, closeAfterSave) {
    setSavingPreferences(true);
    setError('');
    try {
      const { error: saveError } = await supabase.from('coach_birthday_notification_preferences').upsert({
        coach_id: user.id,
        enabled: nextPreferences.enabled,
        channel: nextPreferences.channel,
        weekday: Number(nextPreferences.weekday),
        notification_time: `${nextPreferences.notification_time}:00`,
        timezone: 'Europe/Amsterdam',
      }, { onConflict: 'coach_id' });
      if (saveError) throw saveError;
      showSuccess(t('dashboard.meldingOpgeslagen'));
      if (closeAfterSave) setPreferenceOpen(false);
    } catch (err) {
      setError(t('dashboard.foutOpslaanMelding') + err.message);
    } finally {
      setSavingPreferences(false);
    }
  }

  function togglePreferences() {
    const nextPreferences = { ...preferences, enabled: !preferences.enabled };
    setPreferences(nextPreferences);
    persistPreferences(nextPreferences, false);
  }

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="coach-dashboard">
      <div className="page-header coach-dashboard-header">
        <div>
          <h1 className="page-title">{t('dashboard.begroeting', { naam: firstName(profile?.full_name) })}</h1>
          <p className="page-subtitle">{t('dashboard.weekSubtitel', {
            nummer: format(new Date(), 'w', { locale: nl }),
            van: format(weekStart, 'd MMM', { locale: nl }),
            tot: format(weekEnd, 'd MMM yyyy', { locale: nl }),
          })}</p>
        </div>
        <div className="dashboard-header-actions">
          <button className="btn btn-ghost" onClick={() => navigate('/coach/rooster')}>{t('dashboard.naarRooster')}</button>
          <button className="btn btn-primary" onClick={() => navigate('/coach/les/nieuw')}>{t('dashboard.trainingPlannen')}</button>
        </div>
      </div>

      {error && <div className="error-msg dashboard-error">{error}</div>}

      <div className="dashboard-metrics">
        <Metric label={t('dashboard.actieveLeden')} value={activeClients} context={t('dashboard.eigenClienten')} />
        <Metric label={t('dashboard.trainingenDezeWeek')} value={lessons.length} context={t('dashboard.trainingenVerdeling', { groep: groupLessons.length, pt: lessons.length - groupLessons.length })} />
        <Metric label={t('dashboard.openPlekken')} value={openSpots} context={t('dashboard.groepslessenAantal', { aantal: groupLessons.length })} />
        <Metric label={t('dashboard.bezettingsgraad')} value={`${occupancy}%`} context={t('dashboard.dezeWeek')} />
      </div>

      <div className="dashboard-layout">
        <section className="card dashboard-schedule-card">
          <div className="dashboard-card-head">
            <div><h2>{t('dashboard.vandaagMorgen')}</h2><p>{t('dashboard.eerstvolgendeTrainingen')}</p></div>
            <button className="dashboard-text-button" onClick={() => navigate('/coach/rooster')}>{t('dashboard.volledigRooster')}</button>
          </div>
          {upcomingLessons.length === 0 ? (
            <div className="dashboard-empty">{t('dashboard.geenTrainingenVandaagMorgen')}</div>
          ) : [new Date(), addDays(new Date(), 1)].map(day => {
            const dayLessons = upcomingLessons.filter(lesson => isSameDay(toAmsterdamDate(lesson.starts_at), day));
            if (dayLessons.length === 0) return null;
            return (
              <div className="dashboard-day" key={day.toISOString()}>
                <div className="dashboard-day-label"><span>{format(day, 'EEE', { locale: nl })}</span><strong>{format(day, 'd')}</strong><small>{format(day, 'MMM', { locale: nl })}</small></div>
                <div className="dashboard-lessons">
                  {dayLessons.map(lesson => (
                    <button className="dashboard-lesson" key={lesson.id} onClick={() => navigate('/coach/rooster')}>
                      <span><strong>{lesson.name}</strong><small>{toAmsterdamTime(lesson.starts_at)} · {lesson.duration_minutes} {t('dashboard.minuten')}{lesson.locations?.name ? ` · ${lesson.locations.name}` : ''}</small></span>
                      <span className="dashboard-capacity">{lesson.type === 'personal' ? t('dashboard.pt') : `${enrollmentCounts[lesson.id] || 0}/${lesson.max_participants}`}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </section>

        <aside className="dashboard-side-column">
          <section className="card dashboard-birthday-card">
            <div className="dashboard-card-head birthday-card-head">
              <div><h2>🎂 {t('dashboard.jarigDezeWeek')}</h2><p>{t('dashboard.clientenAantal', { aantal: birthdays.length })}</p></div>
              <button className="dashboard-icon-button" onClick={() => setPreferenceOpen(open => !open)} aria-label={t('dashboard.meldingInstellen')}>⚙️</button>
            </div>
            {birthdays.length === 0 ? <div className="dashboard-empty compact">{t('dashboard.geenVerjaardagen')}</div> : birthdays.map(client => {
              const birthYear = Number(client.date_of_birth?.slice(0, 4));
              const age = client.birthday.getFullYear() - birthYear;
              const dateLabel = isSameDay(client.birthday, new Date()) ? t('dashboard.vandaagJarig') : format(client.birthday, 'EEEE d MMMM', { locale: nl });
              return (
                <button className="birthday-person" key={client.id} onClick={() => navigate(`/coach/clients/${client.id}`)}>
                  <span className="birthday-avatar">{client.full_name?.split(/\s+/).map(part => part[0]).slice(0, 2).join('').toUpperCase()}</span>
                  <span><strong>{client.full_name}</strong><small>{dateLabel}</small></span>
                  <span className="birthday-age">{t('dashboard.jaar', { aantal: age })}</span>
                </button>
              );
            })}

            <div className="birthday-notification-summary">
              <span><strong>{t('dashboard.weekmelding')}</strong><small>{preferences.enabled ? t('dashboard.meldingSamenvatting', {
                kanaal: t(`dashboard.kanaal.${preferences.channel}`), dag: t(`dashboard.weekdag.${preferences.weekday}`), tijd: preferences.notification_time,
              }) : t('dashboard.meldingUit')}</small></span>
              <button className={`dashboard-switch${preferences.enabled ? ' is-on' : ''}`} onClick={togglePreferences} disabled={savingPreferences} aria-pressed={preferences.enabled} aria-label={t('dashboard.weekmelding')}>●</button>
            </div>

            {preferenceOpen && (
              <div className="birthday-preferences">
                <div className="form-group">
                  <label className="form-label" htmlFor="birthday-channel">{t('dashboard.ontvangenVia')}</label>
                  <select id="birthday-channel" className="form-input" value={preferences.channel} onChange={event => setPreferences(current => ({ ...current, channel: event.target.value }))}>
                    <option value="push">{t('dashboard.kanaal.push')}</option><option value="email">{t('dashboard.kanaal.email')}</option><option value="both">{t('dashboard.kanaal.both')}</option>
                  </select>
                </div>
                <div className="birthday-preference-grid">
                  <div className="form-group"><label className="form-label" htmlFor="birthday-day">{t('dashboard.opWelkeDag')}</label><select id="birthday-day" className="form-input" value={preferences.weekday} onChange={event => setPreferences(current => ({ ...current, weekday: Number(event.target.value) }))}>{WEEKDAYS.map(([value, key]) => <option key={value} value={value}>{t(key)}</option>)}</select></div>
                  <div className="form-group"><label className="form-label" htmlFor="birthday-time">{t('dashboard.omWelkeTijd')}</label><input id="birthday-time" type="time" step="300" className="form-input" value={preferences.notification_time} onChange={event => setPreferences(current => ({ ...current, notification_time: event.target.value }))} /></div>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => persistPreferences(preferences, true)} disabled={savingPreferences}>{savingPreferences ? t('dashboard.opslaanBezig') : t('dashboard.voorkeurOpslaan')}</button>
              </div>
            )}
          </section>

          <section className="card dashboard-attention-card">
            <div className="dashboard-card-head"><div><h2>{t('dashboard.vraagtAandacht')}</h2><p>{t('dashboard.openstaandeTaken')}</p></div></div>
            <AttentionRow label={t('dashboard.ptAanvragen')} value={actionSummary.ptAanvragen} onClick={() => navigate('/coach/acties')} />
            <AttentionRow label={t('dashboard.prospectActies')} value={actionSummary.prospectActies} onClick={() => navigate('/coach/acties')} />
            <AttentionRow label={t('dashboard.uitzonderingen')} value={actionSummary.uitzonderingen} onClick={() => navigate('/coach/acties')} />
          </section>

          <section className="card dashboard-unlogged-card">
            <div className="dashboard-card-head">
              <div>
                <h2>{t('dashboard.nietGelogdeSessies')}</h2>
                <p>{t('dashboard.nietGelogdeSessiesAantal', { aantal: nietGelogdeSessies.length, dagen: UNLOGGED_SESSION_LOOKBACK_DAYS })}</p>
              </div>
              {nietGelogdeSessies.length > UNLOGGED_PREVIEW_COUNT && (
                <button className="dashboard-text-button" onClick={() => navigate('/coach/sessies-niet-gelogd')}>{t('dashboard.bekijkAlles')}</button>
              )}
            </div>
            {nietGelogdeSessies.length === 0 ? (
              <div className="dashboard-empty compact">{t('dashboard.geenNietGelogdeSessies')}</div>
            ) : (
              nietGelogdeSessies.slice(0, UNLOGGED_PREVIEW_COUNT).map(item => (
                <button
                  className="unlogged-session-row"
                  key={`${item.lessonId}:${item.clientId}`}
                  onClick={() => navigate('/coach/rooster')}
                >
                  <span><strong>{item.clientName || t('acties.onbekendeClient')}</strong><small>{formatLessonMoment(item.startsAt)}{item.coachName ? ` · ${item.coachName}` : ''}</small></span>
                </button>
              ))
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

function formatLessonMoment(isoStr) {
  if (!isoStr) return '';
  return new Intl.DateTimeFormat('nl-NL', {
    timeZone: 'Europe/Amsterdam', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(new Date(isoStr));
}

function Metric({ label, value, context }) {
  return <section className="card dashboard-metric"><span>{label}</span><strong>{value}</strong><small>{context}</small></section>;
}

function AttentionRow({ label, value, onClick }) {
  return <button className="dashboard-attention-row" onClick={onClick}><span>{label}</span><strong>{t('dashboard.openAantal', { aantal: value })}</strong></button>;
}
